import React from 'react';

import { ANIM, gaugeGradeColor } from '../../theme/tokens';
import { GaugeNumber } from '../GaugeNumber';
import { GaugeWrap, GaugeCenter, GaugeScore, GaugeStatus } from './ScoreGauge.styles';

export type ScoreGaugeProps = {
  score: number;
  status: string;
  /** 0-100. Fraction of the ring that fills. */
  progress: number;
  /** When true, the ring draws in and the number fades + rises into place. */
  animate: boolean;
  /** Desktop ring diameter (px). Scales down on the mobile breakpoint. */
  size?: number;
};

// The ring is bigger on desktop and shrinks on mobile (see GaugeWrap). The
// centred number/status fonts scale with it by the same ratio.
const MOBILE_RATIO = 0.72;

/**
 * Circular score gauge. The ring + status colour is graded from the score
 * value (green > 90, blue > 80, yellow > 60, red ≤ 60 — see gaugeGradeColor).
 * On mount the ring "draws" itself in (stroke-dashoffset) while the centred number
 * fades + rises into place and rolls up from 0 to the score with the same NumberFlow
 * odometer animation the Stats dashboard uses (see `GaugeNumber`).
 */
export const ScoreGauge = ({ score, status, progress, animate, size = 124 }: ScoreGaugeProps): React.ReactElement => {
  const r = (size / 124) * 54;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(100, Math.max(0, progress)) / 100);
  const color = gaugeGradeColor(score);
  const center = size / 2;
  const mobileSize = Math.round(size * MOBILE_RATIO);

  return (
    <GaugeWrap $size={size} $mobileSize={mobileSize}>
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`}>
        {/* The track is the ring's own colour at 15%, as Home's score gauges draw theirs (the user, 2026-10-21). */}
        <circle cx={center} cy={center} r={r} fill="none" stroke={color} strokeOpacity={0.15} strokeWidth="6" />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          // On mount the arc starts empty (offset = full circumference) and
          // eases to its target, so the gauge "draws" itself in.
          strokeDashoffset={animate ? offset : c}
          style={{ transition: `stroke-dashoffset ${ANIM.GAUGE_DURATION_MS}ms ${ANIM.EASE}` }}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <GaugeCenter $animate={animate}>
        <GaugeScore $mobileScale={MOBILE_RATIO}>
          <GaugeNumber value={score} animate={animate} />
        </GaugeScore>
        <GaugeStatus text={status} $color={color} $mobileScale={MOBILE_RATIO} />
      </GaugeCenter>
    </GaugeWrap>
  );
};

export default ScoreGauge;